var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useState, useRef, } from 'react';
import cx from 'classnames';
import throttle from 'lodash/throttle';
import styles from './ChipsGroup.module.scss';
import { ChevronLeftBold, ChevronRightBold } from '../../../foundations/icons';
import { IconButton } from '../../../core/IconButton';
import { isElementScrollableToTheLeft, isElementScrollableToTheRight, scrollElementIntoViewWithinContainer, scrollElementLeftBy, scrollElementRightBy, } from '../../../utils/scroll';
import { useAccessibleMenu } from '../../../core/hooks/useAccessibleMenu';
export var ChipsGroup = function (_a) {
    var _b, _c, _d, _e;
    var scrollable = _a.scrollable, backgroundColor = _a.backgroundColor, className = _a.className, children = _a.children, props = __rest(_a, ["scrollable", "backgroundColor", "className", "children"]);
    var listRef = useRef(null);
    var _f = useState(false), canScrollToTheLeft = _f[0], setCanScrollToTheLeft = _f[1];
    var _g = useState(false), canScrollToTheRight = _g[0], setCanScrollToTheRight = _g[1];
    var containerClassNames = cx(styles.container, (_b = {},
        _b[styles.eggShell] = backgroundColor === 'eggShell',
        _b[styles.scallop] = backgroundColor === 'scallop',
        _b), className);
    var chipsGroupClassNames = cx(styles.chipsGroup, (_c = {},
        _c[styles.scrollable] = scrollable,
        _c));
    var accessibleNavigationProps = useAccessibleMenu(listRef, {
        isHorizontalMenu: true,
        menuItemSelector: 'button',
        focusFirstItemOnMount: false,
    });
    var handleScroll = useCallback(function (event) {
        setCanScrollToTheLeft(isElementScrollableToTheLeft({ element: event.target }));
        setCanScrollToTheRight(isElementScrollableToTheRight({ element: event.target }));
    }, []);
    var handleChildFocus = useCallback(function (itemRef) {
        scrollElementIntoViewWithinContainer({
            element: itemRef.current,
            container: listRef.current,
            offset: 0,
        });
    }, []);
    var handleScrollableArea = useCallback(function () {
        setCanScrollToTheLeft(isElementScrollableToTheLeft({ element: listRef.current }));
        setCanScrollToTheRight(isElementScrollableToTheRight({ element: listRef.current }));
    }, []);
    useEffect(function () {
        handleScrollableArea();
    }, [children, handleScrollableArea]);
    useEffect(function () {
        var checkNavigationThrottled = throttle(handleScrollableArea, 200, {
            trailing: true,
            leading: false,
        });
        window.addEventListener('resize', checkNavigationThrottled);
        return function () {
            checkNavigationThrottled.cancel();
            window.removeEventListener('resize', checkNavigationThrottled);
        };
    }, [handleScrollableArea]);
    useEffect(function () {
        var checkDisplayThrottled = throttle(handleScrollableArea, 200, {
            trailing: false,
            leading: true,
        });
        var intersectionObserver = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.intersectionRatio > 0) {
                    checkDisplayThrottled();
                }
            });
        });
        // The `listRef` element is always rendered, and will be committed to the
        // DOM _before_ the side effect executes.
        intersectionObserver.observe(listRef.current);
        return function () {
            checkDisplayThrottled.cancel();
            intersectionObserver.disconnect();
        };
    }, [handleScrollableArea]);
    return (React.createElement("div", __assign({ className: containerClassNames }, props),
        scrollable && canScrollToTheLeft && (React.createElement(IconButton, { "aria-hidden": true, className: cx(styles.scrollControlLeft, (_d = {},
                _d[styles['scrollControlLeft--eggshell']] = backgroundColor === 'eggShell',
                _d[styles['scrollControlLeft--scallop']] = backgroundColor === 'scallop',
                _d)), tabIndex: -1, onClick: function () { return scrollElementLeftBy({ element: listRef.current, scrollByPercentage: 0.7 }); }, "data-testid": "scroll-left-button" },
            React.createElement(ChevronLeftBold, { size: "xsmall", className: styles.icon }))),
        scrollable && canScrollToTheRight && (React.createElement(IconButton, { tabIndex: -1, "aria-hidden": true, className: cx(styles.scrollControlRight, (_e = {},
                _e[styles['scrollControlRight--eggshell']] = backgroundColor === 'eggShell',
                _e[styles['scrollControlRight--scallop']] = backgroundColor === 'scallop',
                _e)), onClick: function () {
                return scrollElementRightBy({ element: listRef.current, scrollByPercentage: 0.7 });
            }, "data-testid": "scroll-right-button" },
            React.createElement(ChevronRightBold, { size: "xsmall", className: styles.icon }))),
        React.createElement("div", __assign({ ref: listRef, className: chipsGroupClassNames, onScroll: handleScroll, role: "group" }, accessibleNavigationProps), React.Children.map(children, function (child) {
            if (!React.isValidElement(child)) {
                return child;
            }
            return React.cloneElement(child, __assign(__assign({}, child.props), { onFocus: function (itemRef) { return handleChildFocus(itemRef); } }));
        }))));
};
//# sourceMappingURL=index.js.map